import * as React from 'react';
import styled, { StyledComponentClass } from 'styled-components';
import PropTypes from 'prop-types';
import { Action, ActionCreatorsMapObject, bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { history } from '../../../store';
import withGranted, { IWithGrantedProps } from '../../HOC/WithGranted';

import {
  Table,
  Link,
  MainContainer,
  Button,
  Pagination,
} from '../../../components';

import {
  PencilIcon,
  TrashIcon,
  CaretLeftIcon,
  PlusIcon,
} from '../../../assets/icons';

import * as actions from '../../../_actions/location';
import * as actionsSearch from '../../../_actions/search';
import * as actionsModal from '../../../_actions/modal';
import * as actionsLayout from '../../../_actions/layout';

import {
  ADMINISTRATION_CLIENTS_DIVISIONS_LOCATIONS_CREATE,
  ADMINISTRATION_CLIENTS_DIVISIONS_LOCATIONS_EDIT,
  ADMINISTRATION_CLIENTS_DIVISIONS_LOCATIONS_DELETE,
} from '../../../_constants/permissions';
import { createStructuredSelector } from 'reselect';
import { IGlobalStore } from '../../../_reducers/reducers';
import { RouteComponentProps } from 'react-router';
import { IOpenConfirmPrompt } from '../../../_actions/modal';

type StyledType<T = {}> = StyledComponentClass<T, React.StatelessComponent<T>>;
interface IPosition extends StyledType {
  Body?: StyledType;
  Head?: StyledType;
  HeadElement?: StyledType;
  Toolbar?: StyledType;
}

const Position: IPosition = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column; 
`;

Position.Body = styled.div`
  width: 100%; 
  height: 100%; 
`;

Position.Head = styled<{ className?: string }>(({ className, children }) => <header className={className}>
  <div>
    {children}
  </div>
</header>)`
  padding: 20px;
  >div{
    margin: -7.5px;
    display: flex;
    justify-content: space-between;
    flex-wrap: wrap;
  }
`;

Position.HeadElement = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  & > * {
    margin: 7.5px;
  }
`;

Position.Toolbar = styled.header`
  text-align: right;
  height: 15px;
  display: flex;
  align-items: center;
  justify-content: flex-end;
`;

const EditButton: React.SFC<{ onEdit: (id: number) => void, id: number }> = ({
  onEdit,
  id,
}) => {

  function handleEdit() {
    onEdit(id);
  }

  return <Link.Button onClick={handleEdit}><PencilIcon /></Link.Button>;
};

EditButton.propTypes = {
  onEdit: PropTypes.func,
  id: PropTypes.number,
};

const DeleteButton: React.SFC<{ onDelete: (id: number) => void, id: number }> = ({
  onDelete,
  id,
}) => {

  function handleDelete() {
    onDelete(id);
  }

  return <Link.Button onClick={handleDelete}><TrashIcon /></Link.Button>;
};

DeleteButton.propTypes = {
  onDelete: PropTypes.func,
  id: PropTypes.number,
};

interface IListProps extends
  IConnectedProps,
  IConnectedActions,
  IWithGrantedProps,
  RouteComponentProps<{ clientId: string, divisionId: string }> {
}

class List extends React.Component<IListProps> {

  constructor(props) {
    super(props);
    this.handleAddNewClick = this.handleAddNewClick.bind(this);
    this.handleChangePage = this.handleChangePage.bind(this);
    this.renderPreloaderRows = this.renderPreloaderRows.bind(this);
    this.handleItemEdit = this.handleItemEdit.bind(this);
    this.handleItemDeleteRequest = this.handleItemDeleteRequest.bind(this);

    this.props.setSearch(`${this.props.match.url}`, (text) => {
      this.props.getAll(this.props.match.params.divisionId, this.props.perPage, 0, text);
      this.props.setPerPage(props.perPage);
    });
  }

  componentDidMount() {
    this.props.getAll(this.props.match.params.divisionId, this.props.perPage, this.props.skip);
  }

  handleAddNewClick() {
    history.push(`${this.props.match.url}/new`);
  }

  handleChangePage(pageInfo) {
    const {
      perPage,
      skip,
    } = pageInfo;

    this.props.setPerPage(perPage);
    this.props.getAll(this.props.match.params.divisionId, perPage, skip);
  }

  renderPreloaderRows(perPage, status) {
    const rows = [];
    const elementsCount = (status === 'loading') ? perPage - 1 : 0;

    for (let i = 0; i < elementsCount; i++) {
      rows.push(<Table.Row
        key={i}
        colSpan="5"
        rows={perPage}
        isLoading={status === 'loading'}
        cells={[<span key="0">NO LOCATIONS FOUND</span>]}
      />);
    }
    return rows;
  }

  handleItemEdit(id) {
    history.push(`${this.props.match.url}/${id}`);
  }

  handleItemDeleteRequest(id) {
    this.props.prompt()
      .then(() => {
        this.props.remove(id)
          .then(() => {
            this.props.getAll(this.props.match.params.divisionId, this.props.perPage, this.props.skip);
          });
      })
      .catch(() => { });
  }

  render() {
    return (
      <MainContainer title="locations management">
        <Position>
          <Position.Head>
            <Position.HeadElement>
              <Link to={`/admin/clients`}><CaretLeftIcon />Clients List</Link>
              <Link to={`/admin/clients/${this.props.match.params.clientId}/divisions`}><CaretLeftIcon />Divisions List</Link>
            </Position.HeadElement>
            <Position.HeadElement>
              {this.props.isGranted(ADMINISTRATION_CLIENTS_DIVISIONS_LOCATIONS_CREATE) && <Button onClick={this.handleAddNewClick} icon={<PlusIcon />}> Add new</Button>}
            </Position.HeadElement>
          </Position.Head>
          <Position.Body>
            <Table fullHeight>
              <Table.Header columns={[
                { name: 'name', label: 'name' },
                { name: 'city', label: 'city' },
                { name: 'state', label: 'state' },
                { name: 'zip code', label: 'zip code' },
                { name: '', label: '' },
              ]} />
              <Table.Body>
                {
                  this.props.listStatus === 'loaded' &&
                    (this.props.locations && !!this.props.locations.length) ?
                    this.props.locations.map((item, index) =>
                      <Table.Row
                        key={`${index}-${item.id}`}
                        cells={[
                          item.name,
                          item.city,
                          item.state,
                          item.zipCode,
                          <Position.Toolbar key={`cell-toolbar-${index}-${item.id}`} >
                            {
                              this.props.isGranted(ADMINISTRATION_CLIENTS_DIVISIONS_LOCATIONS_EDIT) &&
                              <EditButton onEdit={this.handleItemEdit} id={item.id} />
                            }
                            {
                              this.props.isGranted(ADMINISTRATION_CLIENTS_DIVISIONS_LOCATIONS_DELETE) &&
                              <DeleteButton onDelete={this.handleItemDeleteRequest} id={item.id} />
                            }
                          </Position.Toolbar>
                        ]}
                      />) :
                    <Table.Row
                      key={0}
                      colSpan="5"
                      rows={this.props.perPage}
                      isLoading={this.props.listStatus === 'loading'}
                      cells={[<span key="0">NO LOCATIONS FOUND</span>]}
                    />
                }
                {
                  ((this.props.locations && !!this.props.locations.length) || (this.props.listStatus === 'loading')) &&
                  this.renderPreloaderRows(this.props.perPage, this.props.listStatus)
                }
              </Table.Body>
            </Table>
          </Position.Body>
          {
            <Pagination
              skip={this.props.skip}
              perPage={this.props.perPage}
              count={this.props.totalCount}
              onChange={this.handleChangePage}
            />
          }
        </Position>
      </MainContainer>
    );
  }
}

interface IConnectedProps {
  locations: any;
  listStatus: string;
  totalCount: number;
  skip: number;
  perPage: number;
}

interface IConnectedActions {
  getAll: typeof actions.getAll;
  remove: (id: number) => Promise<void>;
  setSearch: typeof actionsSearch.set;
  prompt: IOpenConfirmPrompt;
  setPerPage: typeof actionsLayout.setPerPage;
}

export default connect(
  createStructuredSelector<IGlobalStore, IConnectedProps>({
    locations: state => state.location.locations,
    listStatus: state => state.location.listStatus,
    totalCount: state => state.location.totalCount,
    skip: state => (state.location.skip || 0),
    perPage: state => state.layout.perPage,
  }),
  dispatch => bindActionCreators<IConnectedActions & ActionCreatorsMapObject, ActionCreatorsMapObject<Action>>({
    getAll: actions.getAll,
    remove: actions.remove as any as (id: number) => Promise<void>,
    setSearch: actionsSearch.set,
    prompt: actionsModal.prompt as any as IOpenConfirmPrompt,
    setPerPage: actionsLayout.setPerPage,
  }, dispatch)
)(withGranted(List));
